import { plainText } from "./HtmlUtil";



// The tenant default locale is one app-wide value (MOBConfig.defaultLocale). MOB-015
// added it as a third argument to translateFromPayload and MOB-060 found that no call
// site ever passed it: all ~85 of them took the two-argument form and fell back to
// "whichever locale happens to sit first in the JSON". It is resolved here instead —
// the store pushes the loaded value in from the doSetMobConfig mutation, and until
// MOBConfig has loaded this matches the store's own `defaultLocale` getter fallback.
const DEFAULT_LOCALE_FALLBACK = "cs";
let tenantDefaultLocale = DEFAULT_LOCALE_FALLBACK;

class LocaleUtil {

  /**
   * Set the tenant default locale used by the payload fallback chain. Called by the
   * Vuex `doSetMobConfig` mutation; a missing/blank value restores "cs", which is what
   * the `defaultLocale` getter reports for an unloaded MOBConfig.
   *
   * @param {string} [locale] - MOBConfig.defaultLocale
   */
  static setDefaultLocale(locale) {
    tenantDefaultLocale =
      typeof locale === "string" && locale !== "" ? locale : DEFAULT_LOCALE_FALLBACK;
  }

  /** The tenant default locale the fallback chain currently uses. */
  static getDefaultLocale() {
    return tenantDefaultLocale;
  }

  /**
   * Resolve a translation from an MSS locale payload ('{"cs":…,"en":…}').
   * A non-payload value (plain string, malformed JSON, array) is returned
   * unchanged. A payload missing the requested locale falls back (MOB-015):
   * locale -> tenant default locale -> first populated locale — never the
   * serialised payload. With nothing populated at all, null is returned so the
   * caller can hide the element instead of rendering garbage.
   *
   * The tenant default is read from module state (MOB-060), not from an argument.
   *
   * @param {string} jsonTranslationString - locale payload or plain string
   * @param {string} locale - requested locale (e.g. "de")
   */
  static translateFromPayload(jsonTranslationString, locale) {
    try {
        let term = JSON.parse(jsonTranslationString);
        if (term == null || typeof term !== "object" || Array.isArray(term)) {
          return jsonTranslationString;
        }
        if (term[locale] !== undefined && term[locale] !== null) {
          return term[locale];
        }
        const isPopulated = (key) =>
          term[key] !== undefined && term[key] !== null && term[key] !== "";
        if (isPopulated(tenantDefaultLocale)) {
          return term[tenantDefaultLocale];
        }
        const firstPopulatedLocale = Object.keys(term).find(isPopulated);
        if (firstPopulatedLocale !== undefined) {
          return term[firstPopulatedLocale];
        }
        return null;
      } catch (e) {
        return jsonTranslationString;
      }
  }

  /**
   * The title of an MSS entity (a meeting point, a location or a PointSell) in the requested
   * locale, for text rendering. The title goes through translateFromPayload, so a plain title
   * comes back unchanged. A missing entity, a missing title and an all-empty payload give '' — never the
   * raw title, because the raw title of an all-empty payload is its JSON (MOB-015).
   *
   * @param {{ title?: string } | null | undefined} entity
   * @param {string} locale - requested locale (e.g. "de")
   * @returns {string}
   */
  static entityTitle(entity, locale) {
    if (entity == null) {
      return "";
    }
    const title = LocaleUtil.translateFromPayload(entity.title, locale);
    return title == null ? "" : title;
  }

  /**
   * The name of an MSS entity (a client skill level today) in the requested locale, for text
   * rendering. The sibling of entityTitle for entities whose label field is `name`. The name
   * goes through translateFromPayload, so a plain name comes back unchanged. A missing entity,
   * a missing name and an all-empty payload give '' — never the raw name, because the raw name
   * of an all-empty payload is its JSON (MOB-015).
   *
   * @param {{ name?: string } | null | undefined} entity
   * @param {string} locale - requested locale (e.g. "de")
   * @returns {string}
   */
  static entityName(entity, locale) {
    if (entity == null) {
      return "";
    }
    const name = LocaleUtil.translateFromPayload(entity.name, locale);
    return name == null ? "" : name;
  }

  // The text of an operator value: all elements removed, all entities decoded (MOB-461, doctrine
  // E87). It calls HtmlUtil.plainText, so there is 1 helper and no regex at a site. Use it only
  // for a place that cannot show HTML: a prop label, an attribute, a text interpolation of a
  // value that is not shown as HTML. Do NOT apply it to a value that goes to v-html: the result
  // is text, and a decoded "&lt;b&gt;" is then live markup. A site that can show HTML renders
  // the raw translated value with $safeHtml. A non-string comes back unchanged.
  static decodeHtmlEntities(value) {
    if (typeof value !== "string") {
      return value;
    }
    return plainText(value);
  }

  // translateFromPayload + plainText, for plain-text rendering sites.
  static translateTextFromPayload(jsonTranslationString, locale) {
    return LocaleUtil.decodeHtmlEntities(
      LocaleUtil.translateFromPayload(jsonTranslationString, locale)
    );
  }

  static transformLocaleToLanguage(locale) {
    if(locale && /^[a-zA-Z]{2}$/.test(locale)) {
      return locale.toUpperCase();
    }
    else {
      return "EN"; // default
    }
  }

}

export default LocaleUtil;
